/**
 * SOLID Review
 * S — Strong. One concern: what the client makes of the CMP's consent — the session's copy, whether advertising is
 *     allowed, whether a decision is one the server must hear of, and the audit record at the policy version in force.
 * O — Good. A new category is a field in the schema; the signature and the rule read it.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Strong. The session part a decision reads, one move, and the record.
 * D — Strong. The advertising rule beside the consent's types, the schemas' types and the builder's part role.
 */
import { isAdvertisingAllowed } from '../ports/IStateCookieConsent';
import type { IStateClientSessionPart } from '../decisions/ClientDecision';
import type { IPropsClientSessionChanges } from '../ports/schemas/ClientSession';
import type {
  IEntityClientConsent, IPayloadConsentState, IPayloadCookieConsent, ICommandCookieConsentRecord,
} from '../ports/schemas/ConsentMoves';

const COOKIE_POLICY = 'COOKIE_POLICY';

/**
 * Which revision of the cookie policy a recorded decision was given against, when the server's documents cannot be
 * read. Moved out of infrastructure/consent/ConsentApiAdapter on 2026-08-17: which policy version a consent decision
 * was recorded against is a DOMAIN fact — GDPR Article 7(1) requires being able to demonstrate what the subject agreed
 * to — not a detail of the HTTP adapter that transmits it. Its own file (`consent/cookiePolicyVersion.ts`) until
 * frontend-domain-oo P6, which folded it into its one reader.
 *
 * A FALLBACK since strict-ports FE4b (§28.201). It read '1.0' while the server's Cookie Policy went to 1.1 on
 * 2026-06-26 (`3fabd2fe`), and every consent since was recorded against a version no longer in force. The record
 * carries the version the server PUBLISHES (`recordOf`); this one is the version in force today.
 */
const COOKIE_POLICY_VERSION = '1.1';

/**
 * THE CMP'S DECISION, KEPT AND RECORDED, AS A SMALL AGGREGATE (frontend-domain-oo P6, 2026-10-26; handbook §28.273) —
 * the decide functions of FE4b (`decisions/legal/cookieConsent.ts`, 2026-10-04), moved from CookieConsentProvider's
 * subscription. Every state the CMP reports becomes the session's consent; advertising is allowed only once it is
 * resolved with ad storage and ad personalisation granted; and a RESOLVED state whose signature (source, region, the
 * five categories) differs from the last one recorded is recorded once. The signature is the session's, so a
 * re-subscribe that replays the same decision records nothing.
 *
 * FIXED (§28.201): the record's policy version was the client's constant, `'1.0'`, since the server's Cookie Policy
 * went to 1.1 on 2026-06-26 (`3fabd2fe`). The version is the server's published Cookie Policy's, the constant only a
 * fallback.
 */
export class CookieConsent implements IStateClientSessionPart {
  private constructor(private consent: IEntityClientConsent) {}

  /** The consent the session holds. */
  static of(consent: IEntityClientConsent): CookieConsent {
    return new CookieConsent(consent);
  }

  /** The audit record of a decision, at the Cookie Policy version the server publishes. */
  static recordOf(command: ICommandCookieConsentRecord): IPayloadCookieConsent {
    const { state, published } = command;
    const policy = published.find((document) => document.type === COOKIE_POLICY);
    const record: IPayloadCookieConsent = {
      categories: state.categories,
      tcString: state.tcString,
      source: state.source,
      region: state.region,
      policyVersion: policy?.version ?? COOKIE_POLICY_VERSION,
    };
    return record;
  }

  private static signatureOf(state: IPayloadConsentState): string {
    const c = state.categories;
    return [state.source, state.region, c.adStorage, c.adUserData, c.adPersonalization, c.analyticsStorage, c.functionality].join('|');
  }

  /**
   * The CMP reported `state`: kept — and true if it is a resolved decision the server has not heard of, marked before
   * the call, as the provider did: a record that fails is not retried for the same decision.
   */
  reported(state: IPayloadConsentState): boolean {
    const signature = CookieConsent.signatureOf(state);
    const record = state.resolved && signature !== this.consent.recordedSignature;
    this.consent = {
      resolved: state.resolved,
      categories: state.categories,
      region: state.region,
      source: state.source,
      advertisingAllowed: state.resolved && isAdvertisingAllowed(state.categories),
      recordedSignature: record ? signature : this.consent.recordedSignature,
    };
    return record;
  }

  sessionFields(): IPropsClientSessionChanges {
    const fields: IPropsClientSessionChanges = { consent: this.consent };
    return fields;
  }
}
